import { FC } from 'react'
import { useSelector } from 'react-redux'
import { unsubscribe } from '../../common/services/firebase/data'
import firebase from 'firebase/compat/app'
import { Navigation } from '@lmc/ui-kit'
import { isBrowser, isTablet } from 'react-device-detect'
import { history } from '../../store'
import { getSections, isRouteSpecificPath } from './utils'
import { Home, User, UserRole } from '@lmc/ts-common'
import { useRouter } from 'hooks/useRouter'
import { ssoLogout } from 'common/services/sso'
import useFeatureFlag from 'hooks/useFeatureFlags'
import { getCleanRoute } from 'hooks/useRouteMatch'
import useHomeType from 'hooks/useHomeType'
import { FrontLinkItem } from './components'
import { useRoleAccess } from 'components/AccessControl'

const isWeb = isBrowser || isTablet

type Props = { currentSectionKey?: string }

export const PrimaryNavigation: FC<Props> = ({ currentSectionKey }) => {
  const router = useRouter()

  const { hasRota } = useHomeType()

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || '',
  )

  const user = useSelector(
    (state: { global: { user: User & { privacyOfficer?: boolean } } }) =>
      state.global.user,
  )

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home,
  )

  const carePlanNominative = useSelector(
    (state: { global: { home: { preferredCarePlansNominative: string } } }) =>
      state.global.home.preferredCarePlansNominative,
  )

  const flags = useFeatureFlag({
    flagNames: ['outcomes_and_goals_v2', 'rostering_paywall_screen'],
  })?.data

  const role = user?.role

  const params = router?.params

  const downloadCentreEnabled = user.role !== UserRole.seniorCarer

  const flagGoalsV2Enabled = flags?.outcomes_and_goals_v2?.enabled
  const flagRosteringPaywallScreenEnabled =
    flags?.rostering_paywall_screen?.enabled

  const goalsEnabled = flagGoalsV2Enabled && home?.features?.goals?.enabled

  const handleSignOut = async () => {
    try {
      if (firebase.apps.length) {
        const token = await firebase.messaging().getToken()
        await unsubscribe(token)
      }
    } finally {
      ssoLogout()
    }
  }

  const hasRolePermissionForSeniorCarer = useRoleAccess(
    [],
    [
      {
        i: 'view',
        this: 'User',
      },
    ],
  )

  const sections = getSections(
    user,
    home,
    preferredPeopleNominative,
    carePlanNominative,
    handleSignOut,
    downloadCentreEnabled,
    hasRota,
    goalsEnabled,
    hasRolePermissionForSeniorCarer,
    flagRosteringPaywallScreenEnabled,
  )
    .filter((item) => item.roles.includes(role))
    .filter((item) => (isWeb ? item.isWeb === isWeb : true))

  const arrayPaths = sections.map((item) => item.key)

  const allTabsInactive = !arrayPaths.includes(currentSectionKey as string)

  const { pathname } = history.location

  const cleanRoute = getCleanRoute(pathname, {
    id: pathname.split('/')[3],
  })

  const flagsRotaTeamMemberNavException =
    hasRota && !cleanRoute.startsWith('/organisation/team/[id]')

  const isSubNavPath =
    (!hasRota && isRouteSpecificPath('/organisation')) ||
    (flagsRotaTeamMemberNavException && isRouteSpecificPath('/organisation')) ||
    isRouteSpecificPath('/todos') ||
    isRouteSpecificPath('/rota')

  return (
    <Navigation
      tabs={sections}
      rightChild={
        <FrontLinkItem user={user} home={home} handleSignOut={handleSignOut} />
      }
      router={{ ...history, params }}
      allTabsInactive={allTabsInactive}
      activeKey={pathname.split('/')?.[1] || currentSectionKey}
      withSubNav={isSubNavPath}
    />
  )
}
